'use client';

import { useEffect, useRef, useState } from 'react';
import NextImage from 'next/image';

import { IMG_HOSTING_URLS } from '@/constants/imgs';

interface IImageProps {
    /** 图片地址，空字符串时不请求图片、直接展示占位 */
    src: string;
    /** 自定义占位内容，未传则使用默认灰色脉冲占位 */
    defaultImage?: React.ReactNode;
}

/** 默认灰色脉冲占位层，覆盖在图片区域之上 */
function PlaceholderComponent() {
    return (
        <div className='absolute inset-0 z-[1] bg-[#f1f1f1]'>
            <div className='absolute inset-0 flex animate-pulse items-center justify-center'>
                <div className='relative size-[48px]'>
                    <NextImage src={IMG_HOSTING_URLS.EMPTY_PLACEHOLDER} alt='empty-placeholder' fill />
                </div>
            </div>
        </div>
    );
}

/** 按是否传入 defaultImage 渲染自定义或默认占位 */
function renderFallback(defaultImage?: React.ReactNode) {
    if (defaultImage) {
        return <div className='absolute inset-0 z-[1] size-full'>{defaultImage}</div>;
    }
    return <PlaceholderComponent />;
}

/**
 * 带占位与错误兜底的图片组件
 *
 * @description
 * 封装 next/image（fill + object-cover），空地址、加载中、加载失败时展示 defaultImage 或默认占位
 *
 * @example
 * <div className="relative h-[80px] w-[60px] overflow-hidden">
 *   <Image src={cover_url} />
 * </div>
 *
 * @example
 * <div className="relative size-[74px] overflow-hidden rounded-full">
 *   <Image src={avatar || ''} defaultImage={<UserIcon />} />
 * </div>
 */
export default function Image(props: IImageProps) {
    const { defaultImage, src } = props;
    // 远端图片是否已 onLoad（loading 与 error 共用同一套占位）
    const [loaded, setLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const prevSrcRef = useRef(src);

    // 仅在 src 真正变化时重置，避免 remount 时把缓存图的 onLoad 结果又打回 false
    if (prevSrcRef.current !== src) {
        prevSrcRef.current = src;
        setLoaded(false);
    }

    // 缓存命中时 onLoad 可能已错过，挂载/换源后根据 complete 补齐 loaded
    useEffect(() => {
        const img = imgRef.current;
        if (img?.complete && img.naturalWidth > 0) {
            setLoaded(true);
        }
    }, [src]);

    // 无有效地址时仅展示占位，不发起图片请求
    if (src === '') {
        return <div className='relative size-full'>{renderFallback(defaultImage)}</div>;
    }

    return (
        <div className='relative size-full'>
            <NextImage
                key={src}
                ref={imgRef}
                src={src}
                alt=''
                fill
                className='object-cover'
                onError={() => setLoaded(false)}
                onLoad={() => setLoaded(true)}
            />
            {/* 加载中 / 加载失败占位 */}
            {!loaded && renderFallback(defaultImage)}
        </div>
    );
}
